import {
  type MetricResultBlock,
  type MetricResultRow,
} from '@myre/app-agent-domain/registry/result-block';
import { type SimpleExport } from '@myre/common-sheet';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import { SheetComponent } from '@myre/ui-primitive-components';

import { Component, computed, inject, input, signal } from '@angular/core';

import { WORDINGS } from '../../../myre-wordings-loader';

import { toSheetCell } from '../../helpers/result-cell.helpers';
import {
  RESULT_PAGE_SIZE,
  ResultHandleService,
} from '../../services/result-handle.service';

/**
 * The whole result, opened from the card.
 *
 * The card shows a preview and says so; this is where the reader goes to see
 * the rest. Rows arrive a page at a time from the handle's ARCHIVE — the same
 * rows the preview above came from, so scrolling further never contradicts what
 * was already on screen. Only the export re-runs the plan, and the file it
 * produces says as much.
 *
 * The perimeter closes the table here exactly as it closes the card: a reader
 * who has just finished reading rows is the one who needs to be told what they
 * are and are not.
 */
@Component({
  selector: 'app-result-table',
  standalone: true,
  imports: [SheetComponent],
  templateUrl: './result-table.component.html',
  styleUrl: './result-table.component.scss',
})
export class ResultTableComponent {
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);
  private readonly _resultHandleService = inject(ResultHandleService);

  public readonly result = input.required<MetricResultBlock>();

  protected readonly _loading = signal(false);
  protected readonly _failed = signal(false);

  /**
   * The rows on screen. Seeded with the preview the card already holds, so the
   * modal opens on content rather than on a spinner.
   */
  private readonly _loaded = signal<readonly MetricResultRow[] | null>(null);

  protected get chatWordings() {
    return this._wordings._library.chat;
  }

  protected readonly _rows = computed<readonly MetricResultRow[]>(
    () => this._loaded() ?? this.result().previewRows,
  );

  protected readonly _sheet = computed<SimpleExport>(() => {
    const { columns } = this.result();

    return {
      headers: columns.map(column => ({ label: column.key })),
      rows: this._rows().map(cells => ({
        cells: cells.map((cell, index) => toSheetCell(cell, columns[index])),
      })),
    };
  });

  /**
   * Whether anything is left to fetch. A block whose handle could not be stored
   * has no archive to page through: the preview is all there is, and the footer
   * count already says how much of the result that is.
   */
  protected readonly _hasMore = computed<boolean>(() => {
    const result = this.result();

    return result.handleId !== null && this._rows().length < result.rowCount;
  });

  protected async loadMore(): Promise<void> {
    const result = this.result();
    const { handleId } = result;

    if (handleId === null || this._loading()) {
      return;
    }

    this._loading.set(true);
    this._failed.set(false);

    try {
      const page = await this._resultHandleService.readRows({
        handleId,
        offset: this._rows().length,
        limit: RESULT_PAGE_SIZE,
      });

      const loaded = [...this._rows(), ...page.rows];

      this._loaded.set(loaded);
    } catch {
      this._failed.set(true);
    } finally {
      this._loading.set(false);
    }
  }
}
